import React, { useState, useEffect } from 'react';
import './oncohome.css';
import { getApiEndpoint } from '../../config/oncorisk-config';
import oncoLogo from '../../../../333333.png';
import oncoLogoLight from '../../../../333333_white.png';

function OncoHome({ setSelectedItem }) {
  // Detect theme from parent container
  const [theme, setTheme] = useState('dark');
  useEffect(() => {
    const checkTheme = () => {
      const isLight = document.documentElement.classList.contains('ocrp-theme-light');
      setTheme(isLight ? 'light' : 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const logoSrc = theme === 'light' ? oncoLogoLight : oncoLogo;

  // Knowledge Base Stats
  const [dbStats] = useState({
    evidence: 11148,
    molecularprofiles: 4016,
    variants: 3890,
    therapies: 578,
    diseases: 435,
    assertions: 169,
    features: 899
  });

  const [quickActions] = useState([
    { id: 'new-analysis', label: 'Upload New Analysis', icon: 'plus-circle', path: 'pipelineanalysis', openModal: true },
    { id: 'browse-knowledge', label: 'Browse Knowledge Base', icon: 'database', path: 'assertions' },
    { id: 'explore-tools', label: 'Perform Deep Query', icon: 'microscope', path: 'query' },
    { id: 'view-docs', label: 'Documentation', icon: 'book', external: true, url: 'https://xiyasong.github.io/OncoRisk/' }
  ]);

 const [latestUpdates] = useState([
  {
    id: 1,
    type: 'database',
    component: 'Knowledge Base',
    date: '2026-01-15',
    description: 'Expanded oncogenic knowledge base with updated CiVIC, ClinVar, OncoKB, and CancerVar integration'
  },
  {
    id: 2,
    type: 'feature',
    component: 'Pipeline Analysis',
    date: '2026-01-12',
    description: 'Refined VEP-based pipeline with detailed transcript prioritization, post-VEP filtering, and improved progress indicators'
  },
  {
    id: 3,
    type: 'integration',
    component: 'API Services',
    date: '2026-01-10',
    description: 'Released public REST API with JSON export support for knowledge base, deep queries, and pipeline results'
  },
  {
    id: 4,
    type: 'update',
    component: 'Web Interface & Usability',
    date: '2026-01-08',
    description: 'Fixed all interactive UI elements, improved layout responsiveness, increased readability, and restored full documentation access'
  }
]);


  const handleQuickAction = (action) => {
    if (action.external && action.url) {
      window.open(action.url, '_blank');
    } else if (action.path && setSelectedItem) {
      setSelectedItem(action.path);
      // If this action should also open the upload modal, dispatch event after navigation
      if (action.openModal) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('ocrp-open-upload-modal'));
        }, 100);
      }
    }
  };

  return (
    <div className="OCRP-home">
      {/* Header with welcome message */}
      <div className="OCRP-home-header">
        <div className="OCRP-welcome-container">
          <div className="OCRP-welcome-logo-wrapper">
            <img src={logoSrc} alt="OncoRisk Logo" className="OCRP-welcome-logo" />
          </div>
          <div className="OCRP-welcome-text">
            <h1>Welcome to OncoRisk</h1>
            <p>Advanced genomic analysis and knowledge platform for precision oncology</p>
          </div>
        </div>
      </div>
      
      {/* Main content area with asymmetric layout */}
      <div className="OCRP-home-content">
        {/* Left Column - split into 2 sections */}
        <div className="OCRP-left-column">
          {/* Top Left - Knowledge Base Stats in creative layout */}
          <div className="OCRP-section OCRP-kb-stats-section">
            <h2>
              <i className="fas fa-database"></i>
              Knowledge Base Overview
            </h2>
            <div className="OCRP-kb-stats-grid-i">
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-file-alt"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.evidence.toLocaleString()}</h3>
                  <p>Evidence Items</p>
                </div>
              </div>
              
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-dna"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.molecularprofiles.toLocaleString()}</h3>
                  <p>Molecular Profiles</p>
                </div>
              </div>
              
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-shield-virus"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.variants.toLocaleString()}</h3>
                  <p>Genomic Variants</p>
                </div>
              </div>
              
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-pills"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.therapies.toLocaleString()}</h3>
                  <p>Therapies</p>
                </div>
              </div>
              
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-disease"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.diseases.toLocaleString()}</h3>
                  <p>Diseases</p>
                </div>
              </div>
              
              <div className="OCRP-kb-stat-card">
                <div className="OCRP-kb-stat-icon-i">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="OCRP-kb-stat-content">
                  <h3>{dbStats.assertions.toLocaleString()}</h3>
                  <p>Clinical Assertions</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Left - Quick Actions */}
          <div className="OCRP-section OCRP-quick-actions-section">
            <h2>
              <i className="fas fa-bolt"></i>
              Quick Actions
            </h2>
            <div className="OCRP-quick-actions-grid">
              {quickActions.map(action => (
                <button 
                  key={action.id} 
                  className="OCRP-action-btn"
                  onClick={() => handleQuickAction(action)}
                >
                  <div className="OCRP-action-icon">
                    <i className={`fas fa-${action.icon}`}></i>
                  </div>
                  <div className="OCRP-action-label">{action.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right Column - Tall panel with Updates and Resources */}
        <div className="OCRP-right-column">
          {/* Updates Panel */}
          <div className="OCRP-section OCRP-updates-section">
            <h2>
              <i className="fas fa-history"></i>
              Latest Updates
            </h2>
            <div className="OCRP-updates-list">
              {latestUpdates.map(update => (
                <div key={update.id} className="OCRP-update-item">
                  <div className="OCRP-update-icon">
                    <i className={`fas fa-${update.type === 'database' ? 'database' : 
                                     update.type === 'feature' ? 'star' : 
                                     update.type === 'integration' ? 'plug' : 'sync'}`}></i>
                  </div>
                  <div className="OCRP-update-content">
                    <div className="OCRP-update-header">
                      <h4>{update.component}</h4>
                      <span className="OCRP-update-date">{update.date}</span>
                    </div>
                    <p>{update.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Resources Section - inside the tall right panel */}
            <div className="OCRP-resources-container">
              <h2>
                <i className="fas fa-tools"></i>
                Additional Resources
              </h2>
              <div className="OCRP-resources-grid">
                <div className="OCRP-resource-card">
                  <div className="OCRP-resource-icon">
                    <i className="fas fa-cogs"></i>
                  </div>
                  <div className="OCRP-resource-content">
                    <h3>Settings</h3>
                    <p>Configure application preferences, API connections, and user access</p>
                    <button className="OCRP-resource-button" onClick={() => setSelectedItem && setSelectedItem('settings')}>
                      <i className="fas fa-arrow-right"></i>
                      Open Settings
                    </button>
                  </div>
                </div>
                
                <div className="OCRP-resource-card">
                  <div className="OCRP-resource-icon">
                    <i className="fas fa-question-circle"></i>
                  </div>
                  <div className="OCRP-resource-content">
                    <h3>Help & Support</h3>
                    <p>Access documentation, tutorials, and contact technical support</p>
                    <button className="OCRP-resource-button" onClick={() => window.open('https://xiyasong.github.io/OncoRisk/', '_blank')}>
                      <i className="fas fa-arrow-right"></i>
                      Get Help
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OncoHome;